/**
 * TD Ala Modal Web Component
 * 
 * A modal that slides up from the bottom of the page
 * Features:
 * - Slides up animation from bottom
 * - Backdrop click to close
 * - Close button support
 * - Escape key to close
 * - Scroll lock when open
 * - Dynamic content fetching with Shopify Section Rendering API
 * - Loading state with spinner
 * - Error handling and retry functionality
 * 
 * Configuration:
 * - data-cache-content: Whether to cache fetched content (default: true)
 * - data-show-loading: Whether to show loading spinner (default: true)
 * 
 * Usage:
 * - Section URL is provided dynamically via event data
 * - TD_PUBLISH('modal-id:open', { sectionUrl: '/path?section_id=content' })
 * - TD_PUBLISH('modal-id:refresh', { sectionUrl: '/path?section_id=content' })
 */
if (!customElements.get('td-ala-modal')) {
  class TdAlaModal extends HTMLElement {
    
    /**
     * Initialize the component when connected to DOM
     */
    connectedCallback() {
      this.initializeInstanceVariables();
      this.setupEventListeners();
    }
  
    /**
     * Initialize instance variables
     */
    initializeInstanceVariables() {
      // Modal state
      this.isOpen = false;
      this.isClosing = false;
      
      // Content fetching state
      this.isLoading = false;
      this.hasError = false;
      this.cachedContent = null;
      this.lastFetchUrl = null;
      this.currentSectionUrl = null;
      
      
      // Find existing DOM elements
      this.backdrop = this.querySelector('[data-backdrop]');
      this.modalContainer = this.querySelector('[data-container]');
      this.modalContent = this.querySelector('[data-content]');
      this.closeButton = this.querySelector('[data-close-button]');
      this.isFullScreen = this.dataset.isFullScreen !== 'false';
      
      // Find template fragments
      this.loadingTemplate = this.querySelector('[data-template="loading"]');
      this.errorTemplate = this.querySelector('[data-template="error"]');
      
      // Get configuration from attributes
      this.modalId = this.dataset.modalId || 'td-modal';
      this.closeOnBackdrop = this.dataset.closeOnBackdrop !== 'false';
      this.closeOnEscape = this.dataset.closeOnEscape !== 'false';
      this.showCloseButton = this.dataset.showCloseButton !== 'false';
      
      // Dynamic content configuration
      this.cacheContent = this.dataset.cacheContent !== 'false';
      this.showLoading = this.dataset.showLoading !== 'false';
      this.retryAttempts = parseInt(this.dataset.retryAttempts) || 3;
      
      // Store initial content as fallback
      this.staticContent = this.modalContent ? this.modalContent.innerHTML : '';
    }
  
    /**
     * Setup all event listeners
     */
    setupEventListeners() {
      this.setupModalEventListeners();
      this.setupGlobalEventListeners();
      this.setupModalInteractionListeners();
    }
  
    /**
     * Setup modal-specific event listeners
     */
    setupModalEventListeners() {
      // Listen for open/close events using custom events
      this.openHandler = (event) => {
        if (event.detail && event.detail.sectionUrl) {
          this.handleOpen(event.detail);
        } else {
          this.handleOpen();
        }
      };
      
      this.closeHandler = () => this.handleClose();
      this.refreshHandler = (event) => this.handleRefreshContent(event.detail || {});
      
      // Add event listeners for custom events
      document.addEventListener(`${this.modalId}:open`, this.openHandler);
      document.addEventListener(`${this.modalId}:close`, this.closeHandler);
      document.addEventListener(`${this.modalId}:refresh`, this.refreshHandler);
    }
  
    /**
     * Setup global event listeners
     */
    setupGlobalEventListeners() {
      // Escape key listener
      if (this.closeOnEscape) {
        this.escapeListener = this.handleEscapeKey.bind(this);
        document.addEventListener('keydown', this.escapeListener);
      }
      
      // Window resize listener to adjust modal height
      this.resizeListener = this.handleWindowResize.bind(this);
      window.addEventListener('resize', this.resizeListener);
    }
  
    /**
     * Setup interaction listeners after render
     */
    setupModalInteractionListeners() {
      // Backdrop click to close
      if (this.closeOnBackdrop && this.backdrop) {
        this.backdrop.addEventListener('click', this.handleBackdropClick.bind(this));
      }
  
      // Close button click to close
      if (this.showCloseButton && this.closeButton) {
        this.closeButton.addEventListener('click', this.handleCloseButtonClick.bind(this));
      }
      
      // Handle any close buttons within modal content
      this.setupContentCloseButtons();
  
      // Prevent clicks inside modal from closing
      if (this.modalContainer) {
        this.modalContainer.addEventListener('click', this.handleModalClick.bind(this));
      }
    }
  
    /**
     * Setup close buttons within modal content
     */
    setupContentCloseButtons() {
      if (this.modalContent) {
        const closeButtons = this.modalContent.querySelectorAll('[data-modal-close]');
        closeButtons.forEach(button => {
          button.addEventListener('click', this.handleContentCloseButtonClick.bind(this));
        });
      }
    }
  
    /**
     * Handle close button click from modal content
     * @param {Event} event 
     */
    handleContentCloseButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.isClosing) {
        this.handleClose();
      }
    }
  
    /**
     * Handle modal open - fetch content if needed
     * @param {Object} eventData - Event data containing sectionUrl and other options
     */
    async handleOpen(eventData = {}) {
      if (this.isOpen || this.isClosing) return;
      
      this.isOpen = true;
      this.isClosing = false;
      this.classList.remove('td-modal--closing');
      this.classList.add('td-modal--open');
      this.lockBodyScroll();

      // Reset modal container styles to ensure visibility
      if (this.modalContainer) {
        this.modalContainer.style.display = 'flex';
        this.modalContainer.style.visibility = 'visible';
        this.modalContainer.style.opacity = '1';
        this.modalContainer.style.height = 'auto';
        this.modalContainer.style.maxHeight = 'none';
      }

      // Set the section URL from event data
      if (eventData.sectionUrl) {
        this.currentSectionUrl = eventData.sectionUrl;
      }

      // Fetch content if URL is provided
      if (this.currentSectionUrl) {
        await this.fetchAndUpdateContent();
      } else {
        // If no new content to fetch, adjust height for existing static content
        requestAnimationFrame(() => {
          this.adjustModalHeight();
        });
      }

      document.dispatchEvent(new CustomEvent(`${this.modalId}:opened`, { 
        detail: { element: this } 
      }));
    }
  
    /**
     * Fetch content from Shopify Section Rendering API
     * @param {number} attemptCount - Current retry attempt
     * @returns {Promise<boolean>} - Success status
     */
    async fetchAndUpdateContent(attemptCount = 0) {
      if (!this.currentSectionUrl) {
        console.warn('TD Ala Modal: No section URL provided for content fetching');
        return false;
      }
  
      // Check if we have cached content and should use it
      if (this.cacheContent && this.cachedContent && this.lastFetchUrl === this.currentSectionUrl) {
        this.updateModalContent(this.cachedContent);
        setTimeout(() => {
          this.adjustModalHeight();
        }, 100);
        return true;
      }
  
      try {
        this.setLoadingState(true);
        this.hasError = false;
  
        const response = await fetch(this.currentSectionUrl, {
          method: 'GET',
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
  
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
  
        const htmlContent = await response.text();
        
        // Cache the content if caching is enabled
        if (this.cacheContent) {
          this.cachedContent = htmlContent;
          this.lastFetchUrl = this.currentSectionUrl;
        }
  
        this.updateModalContent(htmlContent);
        this.setLoadingState(false);
        
        document.dispatchEvent(new CustomEvent(`${this.modalId}:contentLoaded`, { 
          detail: { element: this, content: htmlContent } 
        }));
        
        return true;
      } catch (error) {
        console.error(`TD Ala Modal: Failed to fetch content from ${this.currentSectionUrl}:`, error);
        
        // Retry logic
        if (attemptCount < this.retryAttempts) {
          console.log(`TD Ala Modal: Retrying... (${attemptCount + 1}/${this.retryAttempts})`);
          
          // Exponential backoff: wait 1s, then 2s, then 4s
          const delay = Math.pow(2, attemptCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          
          return await this.fetchAndUpdateContent(attemptCount + 1);
        }
        
        // All retries failed
        this.handleFetchError(error);
        return false;
      }
    }
  
    /**
     * Handle fetch errors by showing error state
     * @param {Error} error - The error that occurred
     */
    handleFetchError(error) {
      this.hasError = true;
      this.setLoadingState(false);
      
      const errorContent = this.getErrorTemplate(error.message);
      this.updateModalContent(errorContent);
      
      document.dispatchEvent(new CustomEvent(`${this.modalId}:contentError`, { 
        detail: { element: this, error } 
      }));
    }
  
    /**
     * Get error template with populated error message
     * @param {string} errorMessage - The error message to display
     * @returns {string} - HTML content for error state
     */
    getErrorTemplate(errorMessage) {
      if (!this.errorTemplate) {
        // Fallback if template is missing
        return `
          <div class="td-modal__error">
            <div class="td-modal__error-icon">⚠️</div>
            <h3 class="td-modal__error-title">Failed to Load Content</h3>
            <p class="td-modal__error-message">${errorMessage}</p>
            <button type="button" class="td-modal__retry-btn" data-retry-action>
              Try Again
            </button>
          </div>
        `;
      }
  
      // Clone the template
      const errorClone = this.errorTemplate.cloneNode(true);
      
      // Update the error message
      const errorMessageElement = errorClone.querySelector('[data-error-message]');
      if (errorMessageElement) {
        errorMessageElement.textContent = errorMessage;
      }
      
      return errorClone.outerHTML;
    }
  
    /**
     * Get loading template
     * @returns {string} - HTML content for loading state
     */
    getLoadingTemplate() {
      if (!this.loadingTemplate) {
        // Fallback if template is missing
        return `
          <div class="td-modal__loading">
            <div class="td-modal__spinner">
              <svg class="td-modal__spinner-icon" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="32" stroke-dashoffset="32">
                  <animate attributeName="stroke-dashoffset" dur="1s" values="32;0" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <p class="td-modal__loading-text">Loading content...</p>
          </div>
        `;
      }
  
      return this.loadingTemplate.outerHTML;
    }
  
    /**
     * Set loading state
     * @param {boolean} isLoading - Whether content is loading
     */
    setLoadingState(isLoading) {
      this.isLoading = isLoading;
      
      if (isLoading) {
        this.classList.add('td-modal--loading');
        if (this.showLoading) {
          this.updateModalContent(this.getLoadingTemplate());
        }
      } else {
        this.classList.remove('td-modal--loading');
      }
    }
  
    /**
     * Update modal content
     * @param {string} htmlContent - New HTML content
     */
    updateModalContent(htmlContent) {
      if (this.modalContent) {
        this.modalContent.innerHTML = htmlContent;
        
        
        // Force content visibility by resetting styles
        this.modalContent.style.display = 'block';
        this.modalContent.style.visibility = 'visible';
        this.modalContent.style.opacity = '1';
        
        // Setup retry button event listener if it exists
        const retryButton = this.modalContent.querySelector('[data-retry-button], [data-retry-action]');
        if (retryButton) {
          retryButton.addEventListener('click', this.handleRetry.bind(this));
        }
        
        // Setup close buttons within the content
        this.setupContentCloseButtons();
        
        // Adjust modal height to fit content after a short delay to ensure DOM is updated
        setTimeout(() => {
          this.adjustModalHeight();
        }, 100);
        
        // Dispatch event for any scripts that need to reinitialize
        document.dispatchEvent(new CustomEvent(`${this.modalId}:contentUpdated`, { 
          detail: { element: this } 
        }));
      }
    }
  
    /**
     * Adjust modal height to fit content
     */
    adjustModalHeight() {
      if (!this.modalContainer || !this.modalContent || this.isFullScreen) return;
      
      // Add transition class for smooth height animation
      this.classList.add('td-modal--resizing');
      this.modalContainer.style.transition = 'height 0.3s ease, max-height 0.3s ease';
      
      // Get the natural height of the content
      const contentHeight = this.modalContent.scrollHeight;
      
      // Calculate maximum available height (viewport height minus some padding)
      const viewportHeight = window.innerHeight;
      const maxHeight = Math.min(viewportHeight * 0.9, viewportHeight - 40); // 90% of viewport or viewport - 40px padding
      
      // Determine the target height
      const targetHeight = Math.min(contentHeight, maxHeight);
      
      // Apply the height to the modal container
      this.modalContainer.style.height = `${targetHeight}px`;
      this.modalContainer.style.maxHeight = `${maxHeight}px`;
      
      // Force container to be visible
      this.modalContainer.style.display = 'flex';
      this.modalContainer.style.visibility = 'visible';
      this.modalContainer.style.opacity = '1';
      
      
      // If content is taller than max height, ensure content area is scrollable
      if (contentHeight > maxHeight) {
        this.modalContent.style.overflowY = 'auto';
        this.modalContent.style.height = '100%';
      } else {
        this.modalContent.style.overflowY = 'visible';
        this.modalContent.style.height = 'auto';
      }
      
      // Remove transition class after animation completes
      setTimeout(() => {
        this.classList.remove('td-modal--resizing');
        this.modalContainer.style.transition = '';
        
      }, 300); // Match transition duration
    }
  
    /**
     * Handle retry button click
     */
    async handleRetry() {
      if (this.currentSectionUrl && !this.isLoading) {
        // Clear cached content to force refetch
        this.cachedContent = null;
        this.lastFetchUrl = null;
        await this.fetchAndUpdateContent();
      }
    }
  
    /**
     * Handle refresh content event - opens modal if not open and fetches content
     * @param {Object} eventData - Event data containing sectionUrl and other options
     */
    async handleRefreshContent(eventData = {}) {
      // Update section URL if provided
      if (eventData.sectionUrl) {
        this.currentSectionUrl = eventData.sectionUrl;
      }
      
      // Clear cached content to force refetch
      this.cachedContent = null;
      this.lastFetchUrl = null;
      
      // If modal is not open, open it first
      if (!this.isOpen && !this.isClosing) {
        await this.handleOpen(eventData);
      } else if (this.currentSectionUrl) {
        // Modal is already open, just refresh the content
        await this.fetchAndUpdateContent();
      }
    }
  
    /**
     * Handle modal close with smooth slide-out animation
     */
    handleClose() {
      if (!this.isOpen || this.isClosing) return;
      
      this.isClosing = true;
      this.classList.add('td-modal--closing');
      
      // Wait for slide-out animation to complete before fully closing
      setTimeout(() => {
        this.isOpen = false;
        this.isClosing = false;
        this.classList.remove('td-modal--open', 'td-modal--closing', 'td-modal--loading');
        this.unlockBodyScroll();
        
        // Reset content to static content and clear section URL
        if (this.currentSectionUrl && this.staticContent) {
          this.updateModalContent(this.staticContent);
        }
        this.currentSectionUrl = null;
        
        document.dispatchEvent(new CustomEvent(`${this.modalId}:closed`, { 
          detail: { element: this } 
        }));
      }, 300); // Match CSS transition duration
    }
  
    /**
     * Handle escape key press
     * @param {KeyboardEvent} event 
     */
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isOpen && !this.isClosing) {
        this.handleClose();
      }
    }
  
    /**
     * Handle backdrop click
     * @param {Event} event 
     */
    handleBackdropClick(event) {
      if (event.target === this.backdrop && !this.isClosing) {
        this.handleClose();
      }
    }
  
    /**
     * Handle close button click
     * @param {Event} event 
     */
    handleCloseButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.isClosing) {
        this.handleClose();
      }
    }
  
    /**
     * Handle clicks inside modal (prevent closing)
     * @param {Event} event 
     */
    handleModalClick(event) {
      event.stopPropagation();
    }
  
  
    /**
     * Lock body scroll when modal is open
     */
    lockBodyScroll() {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('td-modal-open');
    }
  
    /**
     * Unlock body scroll when modal is closed
     */
    unlockBodyScroll() {
      document.body.style.overflow = '';
      document.body.classList.remove('td-modal-open');
    }
  
    /**
     * Handle window resize to adjust modal height
     */
    handleWindowResize() {
      // Debounce resize adjustments to prevent excessive calls
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        if (this.isOpen && !this.isClosing) {
          this.adjustModalHeight();
        }
      }, 150); // Wait 150ms after resize stops
    }
    
  
    /**
     * Cleanup when component is disconnected
     */
    disconnectedCallback() {
      // Clean up event listeners
      if (this.openHandler) {
        document.removeEventListener(`${this.modalId}:open`, this.openHandler);
      }
      if (this.closeHandler) {
        document.removeEventListener(`${this.modalId}:close`, this.closeHandler);
      }
      if (this.refreshHandler) {
        document.removeEventListener(`${this.modalId}:refresh`, this.refreshHandler);
      }
      
      // Clean up global listeners
      if (this.escapeListener) {
        document.removeEventListener('keydown', this.escapeListener);
      }
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
      }
      
      // Clean up timers
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      // Unlock body scroll if modal was open
      if (this.isOpen) {
        this.unlockBodyScroll();
      }
    }
    
    /**
     * Public method to open modal
     */
    open() {
      this.handleOpen();
    }
    
    /**
     * Public method to close modal
     */
    close() {
      this.handleClose();
    }
  }
  
  // Register the custom element only if it hasn't been defined yet
    customElements.define('td-ala-modal', TdAlaModal);
  } 